import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../../../../scss/common/inputs.scss';
import { observer } from 'mobx-react';
const hexToRgb = hex => {
  // Expand shorthand form (e.g. "03F") to full form (e.g. "0033FF")
  var shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  hex = hex.replace(shorthandRegex, function (m, r, g, b) {
    return r + r + g + g + b + b;
  });
  var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16)
  } : {
    r: 0,
    g: 0,
    b: 0
  };
};
const hexWithoutSharp = hex => {
  return hex.substring(1, hex.length);
};
const hslToHex = hsl => {
  // OK
  const h = hsl.h;
  const s = hsl.s / 100;
  const l = hsl.l / 100;
  const a = s * Math.min(l, 1 - l);
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0'); // convert to Hex and prefix "0" if needed
  };

  return `#${f(0)}${f(8)}${f(4)}`;
};
const convertToBaseColor = hex => {
  // OK
  const HSL = hexToHsl(hex);
  return hslToHex({
    h: HSL.h,
    s: 100,
    l: 50
  });
};
const rgbToHsv = rgb => {
  // OK
  let {
    r,
    g,
    b
  } = rgb;
  r /= 255;
  g /= 255;
  b /= 255;
  let v = Math.max(r, g, b),
    c = v - Math.min(r, g, b);
  let h = c && (v === r ? (g - b) / c : v === g ? 2 + (b - r) / c : 4 + (r - g) / c);
  return {
    h: 60 * (h < 0 ? h + 6 : h),
    s: v && c / v * 100,
    v: v * 100
  };
};
const hexToHsv = hex => {
  // OK
  const rgb = hexToRgb(hex);
  return rgbToHsv(rgb);
};
const rgbToHsl = rgb => {
  // OK
  let {
    r,
    g,
    b
  } = rgb;
  r /= 255;
  g /= 255;
  b /= 255;
  let max = Math.max(r, g, b),
    min = Math.min(r, g, b);
  let h,
    s,
    l = (max + min) / 2;
  if (max === min) {
    h = s = 0; // achromatic
  } else {
    var d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
      default:
        break;
    }
    h /= 6;
  }
  return {
    h: h * 360,
    s: s * 100,
    l: l * 100
  };
};
const hexToHsl = hex => {
  return rgbToHsl(hexToRgb(hex));
};
const getInitialBaseColorPinPosition = hex => {
  const HSL = hexToHsl(hex);
  return HSL.h / 360 * 100;
};
const getInitialCurrentColorPinPosition = hex => {
  const hsv = hexToHsv(hex);
  return {
    x: hsv.s,
    // x is exactly s
    y: 100 - hsv.v // y is 100 - v
  };
};

const hsvToRgb = hsv => {
  let {
    h,
    s,
    v
  } = hsv;
  s = s / 100;
  v = v / 100;
  let f = (n, k = (n + h / 60) % 6) => v - v * s * Math.max(Math.min(k, 4 - k, 1), 0);
  return {
    r: f(5) * 255,
    g: f(3) * 255,
    b: f(1) * 255
  };
};
const hsvToHsl = hsv => {
  return rgbToHsl(hsvToRgb(hsv));
};
const sanitaziePercentage = value => {
  if (value < 0) {
    return 0;
  }
  if (value > 100) {
    return 100;
  }
  return value;
};
const SingleDefaultColorPick = observer(({
  color,
  onSelect
}) => {
  return /*#__PURE__*/React.createElement("span", {
    style: {
      backgroundColor: color
    },
    className: "single-default-color-pick",
    onClick: () => {
      if (onSelect) {
        onSelect(color);
      }
    }
  });
});
const ColorPicker = observer(({
  className = '',
  color = '#000000',
  onUpdate,
  selected = false
}) => {
  const baseColorContainerRef = useRef(null);
  const currentColorContainerRef = useRef(null);
  const pickerRef = useRef(null);
  const [currentColor, setCurrentColor] = useState(color);
  const [baseColor, setBaseColor] = useState(convertToBaseColor(color));
  const [currentColorPinPosition, setCurrentColorPinPosition] = useState(getInitialCurrentColorPinPosition(color));
  const [baseColorPinPosition, setBaseColorPinPosition] = useState(getInitialBaseColorPinPosition(color));
  const [showPicker, setShowPicker] = useState(false);
  const [movementType, setMovementType] = useState(null);
  const getXY = useCallback(({
    clientX,
    clientY,
    target,
    touches
  }, withRespectToElement) => {
    const {
      left,
      top
    } = withRespectToElement.getBoundingClientRect();
    if (typeof clientX === 'undefined' && typeof clientY === 'undefined') {
      return {
        x: touches[0].clientX - left,
        y: touches[0].clientY - top
      };
    }
    return {
      x: clientX - left,
      y: clientY - top
    };
  }, []);
  const startUpdateBaseColor = useCallback((e, movementType) => {
    if (movementType !== null) {
      return;
    }
    setMovementType('base');
    const {
      x
    } = getXY(e, baseColorContainerRef.current);
    let percentage = x / baseColorContainerRef.current.clientWidth * 100;
    percentage = sanitaziePercentage(percentage);
    setBaseColorPinPosition(percentage);
    const newBaseColor = hslToHex({
      h: percentage / 100 * 360,
      s: 100,
      l: 50
    });
    setBaseColor(newBaseColor);
    const xPercentage = currentColorPinPosition.x;
    const yPercentage = currentColorPinPosition.y;
    const newHSVColor = {
      h: hexToHsv(newBaseColor).h,
      s: xPercentage,
      v: 100 - yPercentage
    };
    const newCurrentColor = hslToHex(hsvToHsl(newHSVColor));
    setCurrentColor(newCurrentColor);
    if (onUpdate) {
      onUpdate(newCurrentColor);
    }
  }, [getXY, currentColorPinPosition, onUpdate]);
  const moveUpdateBaseColor = useCallback((e, movementType) => {
    if (movementType !== 'base') {
      return;
    }
    const {
      x
    } = getXY(e, baseColorContainerRef.current);
    let percentage = x / baseColorContainerRef.current.clientWidth * 100;
    percentage = sanitaziePercentage(percentage);
    setBaseColorPinPosition(percentage);
    const newBaseColor = hslToHex({
      h: percentage / 100 * 360,
      s: 100,
      l: 50
    });
    setBaseColor(newBaseColor);
    const xPercentage = currentColorPinPosition.x;
    const yPercentage = currentColorPinPosition.y;
    const newHSVColor = {
      h: hexToHsv(newBaseColor).h,
      s: xPercentage,
      v: 100 - yPercentage
    };
    const newCurrentColor = hslToHex(hsvToHsl(newHSVColor));
    setCurrentColor(newCurrentColor);
    if (onUpdate) {
      onUpdate(newCurrentColor);
    }
  }, [getXY, currentColorPinPosition, onUpdate]);
  const endUpdateBaseColor = useCallback((e, movementType) => {
    if (movementType !== 'base') {
      return;
    }
    setMovementType(null);
  }, []);
  const startUpdateCurrentColor = useCallback((e, movementType) => {
    if (movementType !== null) {
      return;
    }
    setMovementType('current');
    const {
      x,
      y
    } = getXY(e, currentColorContainerRef.current);
    let xPercentage = x / currentColorContainerRef.current.clientWidth * 100;
    let yPercentage = y / currentColorContainerRef.current.clientHeight * 100;
    xPercentage = sanitaziePercentage(xPercentage);
    yPercentage = sanitaziePercentage(yPercentage);
    setCurrentColorPinPosition({
      x: xPercentage,
      y: yPercentage
    });
    const newHSVColor = {
      h: hexToHsv(baseColor).h,
      s: xPercentage,
      v: 100 - yPercentage
    };
    const newCurrentColor = hslToHex(hsvToHsl(newHSVColor));
    setCurrentColor(newCurrentColor);
    if (onUpdate) {
      onUpdate(newCurrentColor);
    }
  }, [getXY, baseColor, onUpdate]);
  const moveUpdateCurrentColor = useCallback((e, movementType) => {
    if (movementType !== 'current') {
      return;
    }
    const {
      x,
      y
    } = getXY(e, currentColorContainerRef.current);
    let xPercentage = x / currentColorContainerRef.current.clientWidth * 100;
    let yPercentage = y / currentColorContainerRef.current.clientHeight * 100;
    xPercentage = sanitaziePercentage(xPercentage);
    yPercentage = sanitaziePercentage(yPercentage);
    setCurrentColorPinPosition({
      x: xPercentage,
      y: yPercentage
    });
    const newHSVColor = {
      h: hexToHsv(baseColor).h,
      s: xPercentage,
      v: 100 - yPercentage
    };
    const newCurrentColor = hslToHex(hsvToHsl(newHSVColor));
    setCurrentColor(newCurrentColor);
    if (onUpdate) {
      onUpdate(newCurrentColor);
    }
  }, [getXY, baseColor, onUpdate]);
  const endUpdateCurrentColor = useCallback((e, movementType) => {
    if (movementType !== 'current') {
      return;
    }
    setMovementType(null);
  }, []);
  const handleChangeCurrentColorText = useCallback(newColor => {
    setCurrentColor(newColor);
    setBaseColor(convertToBaseColor(newColor));
    setCurrentColorPinPosition(getInitialCurrentColorPinPosition(newColor));
    setBaseColorPinPosition(getInitialBaseColorPinPosition(newColor));
    if (onUpdate) {
      onUpdate(newColor);
    }
  }, [onUpdate]);

  // For click outside of color picker
  useEffect(() => {
    document.addEventListener('mousedown', event => {
      if (pickerRef.current && !pickerRef.current.contains(event.target)) {
        setShowPicker(false);
      }
    });
  }, []);
  return /*#__PURE__*/React.createElement("span", {
    className: `${className} color-picker`,
    style: {
      backgroundColor: currentColor
    },
    onClick: () => {
      setShowPicker(true);
    }
  }, showPicker ? /*#__PURE__*/React.createElement("div", {
    className: "color-picker-popup",
    ref: pickerRef,
    onMouseLeave: e => {
      endUpdateCurrentColor(e, movementType);
      endUpdateBaseColor(e, movementType);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "color-picker-cover"
  }, /*#__PURE__*/React.createElement("div", {
    className: "color-picker-saturation",
    ref: currentColorContainerRef,
    style: {
      backgroundColor: baseColor
    },
    onMouseMove: e => {
      moveUpdateCurrentColor(e, movementType);
    },
    onMouseUp: e => {
      endUpdateCurrentColor(e, movementType);
    },
    onMouseDown: e => {
      startUpdateCurrentColor(e, movementType);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "color-picker-saturation-white"
  }), /*#__PURE__*/React.createElement("div", {
    className: "color-picker-saturation-black"
  }), /*#__PURE__*/React.createElement("div", {
    className: "color-picker-saturation-pointer",
    style: {
      left: `calc(${currentColorPinPosition.x}% - 4px)`,
      // -4px per centrare correttamente (dimensione pin 8px)
      top: `calc(${currentColorPinPosition.y}% - 4px)`
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "color-picker-controls"
  }, /*#__PURE__*/React.createElement("div", {
    className: "color-picker-horizontal",
    ref: baseColorContainerRef,
    onMouseMove: e => {
      moveUpdateBaseColor(e, movementType);
    },
    onMouseUp: e => {
      endUpdateBaseColor(e, movementType);
    },
    onMouseDown: e => {
      startUpdateBaseColor(e, movementType);
    }
  }, /*#__PURE__*/React.createElement("div", {
    className: "color-picker-horizontal-pointer",
    style: {
      left: `calc(${baseColorPinPosition}% - 3px)` // -3px per centrare correttamente (dimensione pin 6px)
    }
  }))), /*#__PURE__*/React.createElement("div", {
    className: "color-picker-color-preview"
  }, /*#__PURE__*/React.createElement("span", {
    className: "color-current-color-preview",
    style: {
      backgroundColor: currentColor
    }
  }), /*#__PURE__*/React.createElement("input", {
    type: "text",
    value: `#${hexWithoutSharp(currentColor)}`,
    onChange: e => {
      let newValue = e.target.value;
      if (newValue === '') {
        newValue = '#';
      }
      handleChangeCurrentColorText(newValue);
    }
  })), /*#__PURE__*/React.createElement("div", {
    className: "color-picker-default-colors"
  }, /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#8CA140",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#7DD3C8",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#F4CF57",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#E9732F",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#539CF4",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#C76497",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#7C346C",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#217333",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#214B9D",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }), /*#__PURE__*/React.createElement(SingleDefaultColorPick, {
    color: "#B14040",
    onSelect: newValue => {
      handleChangeCurrentColorText(newValue);
    }
  }))) : null);
});
export default ColorPicker;